import { FirebaseError } from 'firebase'
import { action, observable } from 'mobx'
import { useLocalStore } from 'mobx-react-lite'
import React from 'react'

import { useFirebase } from '../atoms/Firebase'
import { logError } from '../helpers/logger'

interface IOptions {
  subscribe?: boolean
  skip?: boolean
}

const defaultOptions: IOptions = {
  subscribe: false,
  skip: false,
}

export function useFirebaseQuery<TData = {}>(
  path: string | string[],
  options: IOptions = defaultOptions,
) {
  const { ref } = useFirebase()

  const state = useLocalStore(
    source => ({
      loading: true,
      data: null as Maybe<TData>,
      error: null as Maybe<Error>,
      get pathRef() {
        return ref(
          Array.isArray(source.path) ? source.path.join('/') : source.path,
        )
      },
    }),
    { path, skip: options.skip },
  )

  const setData = action((data: TData) => {
    state.data = data
    state.loading = false
  })

  const onSnapshot = React.useCallback(
    (snapshot: Maybe<firebase.database.DataSnapshot>) => {
      if (snapshot && snapshot.exists()) {
        setData(observable.object(snapshot.val(), {}, { deep: false }))
      } else {
        state.error = new Error(
          `No data found at path ${state.pathRef!.toString()}`,
        )
      }
    },
    [],
  )

  const onError = React.useCallback((err: FirebaseError) => {
    logError(err.code, err)
    state.error = err
  }, [])

  React.useEffect(() => {
    if (!options.skip) {
      if (options.subscribe === true) {
        state.pathRef.on('value', onSnapshot, onError)
      } else {
        state.pathRef.once('value', onSnapshot, onError)
      }
    }
    return () => {
      state.pathRef.off('value', onSnapshot)
    }
  }, [path, options.subscribe, options.skip])

  return state
}
